import { Link, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSeo } from "../hooks/useSeo";
import { buildCanonicalUrl } from "../seo/config";
import type { LayoutOutletContext } from "../layout/types";

interface Segment {
  title: string;
  body: string;
}

interface Cta {
  label: string;
  href: string;
}

export const TheQuizFinal = () => {
  const { t } = useTranslation();
  const { localePrefix, locale } = useOutletContext<LayoutOutletContext>();
  const pageTitle = t("pages.theQuizFinal.title");
  const siteTitle = t("site.title");
  const intro = t("pages.theQuizFinal.intro");
  
  useSeo({
    title: `${pageTitle} | ${siteTitle}`,
    description: intro,
    canonical: buildCanonicalUrl("event-details/", locale),
    locale,
  });

  const segments = (t("pages.theQuizFinal.segments", {
    returnObjects: true,
    defaultValue: [],
  }) ?? []) as Segment[];

  const cta = (t("pages.theQuizFinal.cta", {
    returnObjects: true,
    defaultValue: null,
  }) ?? null) as Cta | null;

  const base = localePrefix ? `/${localePrefix}` : "";
  const getLocalePath = (path: string) => {
    if (!path) {
      return base ? `${base}/` : "/";
    }
    const normalised = path.startsWith("/") ? path.slice(1) : path;
    return `${base}/${normalised}/`;
  };

  const isEnglish = locale === "en";
  const showSegments = !isEnglish && segments.length > 0;
  const ctaHref = cta?.href ?? "";
  const ctaLabel = cta?.label ?? "";
  const hasCta = !isEnglish && !!ctaLabel;

  const chinesePageHref = "/event-details/";
  const englishButtonImage = `${import.meta.env.BASE_URL}assets/quiz-final/final-viewchi-en.png`;
  const englishButtonAlt = t("pages.theQuizFinal.buttonAlt", {
    defaultValue: "View the Chinese version of this page",
  });
  const cornerGraphicSrc = `${import.meta.env.BASE_URL}assets/quiz-final/quiz-final-picture.png`;
  const cornerGraphicAlt = t("pages.theQuizFinal.cornerGraphicAlt", {
    defaultValue: "Quiz final highlight graphic",
  });

  return (
    <article className="page page--final quiz-final">
      <div className="quiz-final__content">
        {intro ? <p className="quiz-final__intro">{intro}</p> : null}

        {isEnglish ? (
          <div className="quiz-final__link-wrapper">
            <Link
              to={chinesePageHref}
              className="quiz-final__button"
              aria-label={englishButtonAlt}
            >
              <img src={englishButtonImage} alt={englishButtonAlt} />
            </Link>
          </div>
        ) : (
          <>
            {showSegments ? (
              <div className="card-grid card-grid--center">
                {segments.map((segment) => (
                  <section key={segment.title} className="card-grid__item">
                    <h2 className="card-grid__title">{segment.title}</h2>
                    <p className="card-grid__body">{segment.body}</p>
                  </section>
                ))}
              </div>
            ) : null}

            {hasCta ? (
              <div className="page__cta">
                <Link to={getLocalePath(ctaHref)} className="button-link">
                  {ctaLabel}
                </Link>
              </div>
            ) : null}
          </>
        )}
        <div className="quiz-final__corner-graphic">
          <img src={cornerGraphicSrc} alt={cornerGraphicAlt} />
        </div>
      </div>
    </article>
  );
};
